import { AuthAPIApi, ServerConfiguration, createConfiguration } from '@demand-iq/auth-sdk';
import { SESSION_STORAGE_KEYS } from 'constants/sessionStorage';
import { companyUserAccessToken } from 'hooks/useToken';
import { companyListing } from 'routes/routes';
import { clientConfig } from './clientConfig';
import { ensureFreshUserToken, getTokenExpiry } from './userTokenRefresh';

export const companyIdStorageKey = 'company-id';
/** Legacy key: no longer written (expiry is read from the token), still cleared on exit. */
export const companyExpirationKey = 'company-expiration';

const spoofServer = new ServerConfiguration(clientConfig.url, {});

/** The company currently being spoofed, or null when the admin is in their own company. */
export const getSpoofedCompanyId = (): string | null => sessionStorage.getItem(companyIdStorageKey);

export const getSpoofToken = (): string | null => sessionStorage.getItem(companyUserAccessToken);

export const getSpoofTokenExpiry = (): Date | null => getTokenExpiry(getSpoofToken());

export const clearSpoofSession = (): void => {
  sessionStorage.removeItem(companyUserAccessToken);
  sessionStorage.removeItem(companyIdStorageKey);
  sessionStorage.removeItem(companyExpirationKey);
};

/**
 * Ends a spoof session from outside React and returns the admin to the company list.
 *
 * This is a full page load, so a toast fired here would never paint. Instead a one-shot
 * notice is left in sessionStorage and `CompanyListing` surfaces it on arrival.
 */
export const endSpoofSession = (): void => {
  clearSpoofSession();
  sessionStorage.setItem(SESSION_STORAGE_KEYS.SPOOF_SESSION_ENDED_NOTICE, 'true');
  window.location.href = companyListing;
};

let mintInProgress: Readonly<{ companyId: string; promise: Promise<string | null> }> | null = null;

/**
 * Mints a spoof token for `companyId`, refreshing the admin's own token first when needed.
 *
 * This is the only place a spoof token is created. The `loginAs` path comes from the SDK
 * (`POST /api/v1.0/auth/loginAs/{id}`) so it cannot drift from the route core-api serves.
 * A dedicated client is used rather than the shared `AuthApi` singleton: that one force-logs
 * out on any 401, which is right for a dead refresh token but wrong here — a failed re-mint
 * should end the spoof, not the admin's own session.
 *
 * Returns the new token, or null on any failure. Callers decide what a failure means.
 */
export const mintSpoofToken = async (companyId: string): Promise<string | null> => {
  // Concurrent callers for the same company share one request; a switch to a different
  // company must not be handed the previous company's token.
  if (mintInProgress?.companyId === companyId) {
    return mintInProgress.promise;
  }

  const promise = (async (): Promise<string | null> => {
    // loginAs is authenticated with the admin's own token, which expires on the same
    // 20-minute clock as the spoof token, so make sure it is current first.
    const userToken = await ensureFreshUserToken();
    if (!userToken) {
      return null;
    }

    const client = new AuthAPIApi(
      createConfiguration({
        baseServer: spoofServer,
        authMethods: { bearerToken: { accessToken: userToken } },
      })
    );

    try {
      const response = await client.handleSpoof({ id: companyId });
      if (!response?.accessToken) {
        return null;
      }
      sessionStorage.setItem(companyUserAccessToken, response.accessToken);
      sessionStorage.setItem(companyIdStorageKey, companyId);
      return response.accessToken;
    } catch {
      return null;
    }
  })();

  mintInProgress = { companyId, promise };
  try {
    return await promise;
  } finally {
    if (mintInProgress?.promise === promise) {
      mintInProgress = null;
    }
  }
};
